import { useEffect } from 'react';

const MOTION_SELECTOR = '[data-motion]';
const VISIBLE_CLASS = 'is-motion-visible';
const READY_CLASS = 'is-motion-ready';
const GROUP_ATTR = 'data-motion-group';
const DESKTOP_GROUP_MEDIA_QUERY = '(min-width: 1024px)';
const THRESHOLD_ATTR = 'data-motion-threshold';
const ROOT_MARGIN_ATTR = 'data-motion-root-margin';
const DEFAULT_THRESHOLD = 0.16;
const DEFAULT_ROOT_MARGIN = '0px 0px -8% 0px';

function shouldReduceMotion() {
  if (typeof window === 'undefined') return true;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export default function useRevealMotion(triggerKey) {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const nodes = Array.from(document.querySelectorAll(MOTION_SELECTOR));
    if (!nodes.length) return undefined;

    if (shouldReduceMotion()) {
      nodes.forEach(node => node.classList.add(VISIBLE_CLASS));
      return undefined;
    }

    nodes.forEach(node => {
      node.classList.add(READY_CLASS);
      node.classList.remove(VISIBLE_CLASS);
    });

    const groupedNodes = nodes.reduce((acc, node) => {
      const groupId = node.getAttribute(GROUP_ATTR);
      if (!groupId) return acc;
      if (!acc.has(groupId)) acc.set(groupId, []);
      acc.get(groupId).push(node);
      return acc;
    }, new Map());

    const observerMap = new Map();

    const getObserverKey = node => {
      const thresholdRaw = node.getAttribute(THRESHOLD_ATTR);
      const threshold = Number.parseFloat(thresholdRaw);
      const safeThreshold = Number.isFinite(threshold)
        ? Math.min(1, Math.max(0, threshold))
        : DEFAULT_THRESHOLD;
      const rootMargin = node.getAttribute(ROOT_MARGIN_ATTR) || DEFAULT_ROOT_MARGIN;
      return `${safeThreshold}|${rootMargin}`;
    };

    const getObserver = (key, node) => {
      if (observerMap.has(key)) return observerMap.get(key);

      const [thresholdText, rootMargin] = key.split('|');
      const threshold = Number.parseFloat(thresholdText);
      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;

            const groupId = entry.target.getAttribute(GROUP_ATTR);
            const shouldRevealGroupOnDesktop =
              Boolean(groupId) && window.matchMedia(DESKTOP_GROUP_MEDIA_QUERY).matches;

            if (shouldRevealGroupOnDesktop) {
              const peers = groupedNodes.get(groupId) || [];
              peers.forEach(peer => {
                peer.classList.add(VISIBLE_CLASS);
                const peerKey = getObserverKey(peer);
                const peerObserver = observerMap.get(peerKey);
                peerObserver?.unobserve(peer);
              });
              return;
            }

            entry.target.classList.add(VISIBLE_CLASS);
            observer.unobserve(entry.target);
          });
        },
        {
          threshold,
          rootMargin,
        }
      );

      observerMap.set(key, observer);
      return observer;
    };

    nodes.forEach(node => {
      const key = getObserverKey(node);
      const observer = getObserver(key, node);
      observer.observe(node);
    });

    return () => {
      observerMap.forEach(observer => observer.disconnect());
    };
  }, [triggerKey]);
}
